// src/components/FlowCanvas.tsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import CategoryNode from "./CategoryNode";
import {
  // collections,
  // kpis,
  // systems,
  // categories,
  useFetchSystem,
} from "./sampledata";
import {
  DataSecurityType,
  EffectivenessType,
  InformationCollectionToolType,
} from "../../utils/types";

const nodeTypes = {
  category: CategoryNode,
};

// Define layer positions - revised for 3 clear sections
const LAYER_SPACING = 250;
const KPI_LAYER_Y = 80;
const SYSTEM_LAYER_Y = KPI_LAYER_Y + LAYER_SPACING;
const COLLECTION_LAYER_Y = SYSTEM_LAYER_Y + LAYER_SPACING;
const DEPARTMENT_LAYER_Y = COLLECTION_LAYER_Y + LAYER_SPACING;

export function getContrastingTextColor(bgColor: string): string {
  // Handle undefined, null, or empty color values
  if (!bgColor || typeof bgColor !== "string") {
    return "#000"; // Default to black text
  }

  const hex = bgColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#000" : "#fff";
}

const tailwindColorMap: Record<string, string> = {
  "bg-blue-200": "#BFDBFE",
  "bg-red-200": "#F57B42",
  // "bg-red-200": "#FECACA",
  "bg-yellow-200": "#FEF08A",
  "bg-purple-200": "#E9D5FF",
  "bg-green-200": "#BBF7D0",
  "bg-orange-200": "#FED7AA",
  "bg-pink-200": "#FBCFE8",
};

export function tailwindToHex(twClass: string): string {
  return tailwindColorMap[twClass] ?? "#ccc";
}

const typeColorMap: Record<InformationCollectionToolType, string> = {
  "Paper Based": "#1E3A8A", // Strong Blue
  "Paper Based with Digital Integration": "#B91C1C", // Strong Red
  "In-House Digital Tool": "#CA8A04", // Strong Yellow-Gold
  "Open Digital Tool": "#7E22CE", // Strong Purple
  "Proprietary Digital Tool": "#15803D", // Strong Green
  "Mobile App": "#C2410C", // Strong Orange
  "Web Based Survey": "#BE185D", // Strong Pink
  "Sensors and IoT Devices": "#0F172A", // Dark Navy
  "Don’t Know": "#525252", // Solid Gray
};

export function typeToHex(twClass: InformationCollectionToolType): string {
  return typeColorMap[twClass] ?? "#ccc";
}

const effectivenessColorMap: Record<
  EffectivenessType | DataSecurityType,
  string
> = {
  "Don’t Know": "#525252", // Solid Gray
  Ineffective: "#B91C1C", // Strong Red
  Basic: "#DC2626", // Vivid Red
  "Moderately Effective": "#D97706", // Amber/Orange
  Minimal: "#CA8A04", // Golden Yellow
  Effective: "#15803D", // Strong Green
  Advanced: "#166534", // Deeper Green
};

export function effectivenessToHex(
  twClass: EffectivenessType | DataSecurityType
): string {
  return effectivenessColorMap[twClass] ?? "#ccc";
}

function getCenteredX(
  index: number,
  total: number,
  nodeWidth = 200,
  spacing = 50,
  containerWidth = 1200
) {
  const totalWidth = total * nodeWidth + (total - 1) * spacing;
  const startX = Math.max((containerWidth - totalWidth) / 2, 0); // prevent negative
  return startX + index * (nodeWidth + spacing);
}

const FlowCanvas: React.FC<{
  usecase?: "type" | "effectiveness";
  onClick?: (d?: string) => void;
  orgData?: string;
}> = ({ usecase, onClick, orgData }) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );
  const [activeSystemId, setActiveSystemId] = useState<string | null>(null),
    { systems, categories, kpis, collections } = useFetchSystem({
      organization: orgData,
    });

  const filteredSystems = systems.filter(
    (s) =>
      // selectedCategoryId ? s?.categories?.includes(selectedCategoryId) :
      true
  );

  const visibleKPIs = kpis.filter((kpi) =>
    // activeSystemId
    // 	? kpi?.systemIds.includes(activeSystemId)
    // 	:
    kpi?.systemIds.some((id) => filteredSystems.map((s) => s?.id).includes(id))
  );

  // const visibleKPIs = kpis.filter(kpi => {
  // 	if (activeSystemId) {
  // 		return kpi.systemIds.includes(activeSystemId);
  // 	} else if (selectedCategoryId) {
  // 		const systemIdsInCategory = systems
  // 			.filter(sys => sys.categories?.includes(selectedCategoryId))
  // 			.map(sys => sys.id);

  // 		return kpi.systemIds.some(id => systemIdsInCategory.includes(id));
  // 	}

  // 	return false; // No selection = hide all KPIs
  // });

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const nodes: Node[] = useMemo(() => {
    const nodes: Node[] = [
      {
        id: "kpi-label-left",
        type: "category",
        position: { x: 0, y: KPI_LAYER_Y },
        data: {
          label: "KPIs",
          background: "#e0e0e0",
          orientation: "vertical",
          isLabel: true,
        },
      },
      {
        id: "systems-label-left",
        type: "category",
        position: { x: 0, y: SYSTEM_LAYER_Y },
        data: {
          label: "Data Systems",
          background: "#e0e0e0",
          orientation: "vertical",
          isLabel: true,
        },
      },
      {
        id: "collections-label-left",
        type: "category",
        position: { x: 0, y: COLLECTION_LAYER_Y },
        data: {
          label: "Data Collections",
          background: "#e0e0e0",
          orientation: "vertical",
          isLabel: true,
        },
      },
      {
        id: "departments-label-left",
        type: "category",
        position: { x: 0, y: DEPARTMENT_LAYER_Y },
        data: {
          label: "Departments",
          background: "#e0e0e0",
          orientation: "vertical",
          isLabel: true,
        },
      },
    ];

    // CATEGORY nodes (bottom)
    categories.forEach((cat, i) => {
      const textColor = getContrastingTextColor(cat?.color || "#f5f5f5");

      nodes.push({
        id: `cat-${cat?.id}`,
        position: {
          x: getCenteredX(i, categories.length, 180, 50, containerWidth) + 50,
          y: DEPARTMENT_LAYER_Y,
        },
        data: {
          label: cat?.name,
          color: cat?.color,
          opacity:
            selectedCategoryId && selectedCategoryId !== cat?.id ? 0.2 : 1,
          boxType: "department",
        },
        type: "category",
        style: {
          background: cat?.color,
          color: textColor,
          fontWeight: "bold",
          padding: 10,
          borderRadius: 8,
          minWidth: 120,
          textAlign: "center",
          opacity:
            selectedCategoryId && selectedCategoryId !== cat?.id ? 0.2 : 1,
          cursor: "pointer",
        },
      });
    });

    // COLLECTION node (lower middle)
    collections.forEach((collection, i) => {
      // Determine opacity based on selection

      let isFaded = false;

      if (selectedCategoryId) {
        // Fade collections NOT connected to the selected category
        isFaded = !collection.categories.includes(selectedCategoryId);
      } else if (activeSystemId) {
        // When system selected, collections not faded
        isFaded = false;
      }

      const opacity = isFaded ? 0.2 : 1;

      // Color logic: if category selected and collection has that category, use category color, else collection.color
      let color = collection.color;
      if (
        selectedCategoryId
        // &&
        // collection.categories.includes(selectedCategoryId)
      ) {
        const cat = categories.find((c) => c.id === selectedCategoryId);
        if (cat) color = cat.color;
      } else {
        const cat = categories.find(
          (c) => c.id === collection?.categories?.[0]
        );
        if (cat) color = cat.color;
      }

      const textColor = getContrastingTextColor(color || "#f5f5f5");

      nodes.push({
        id: collection.id,
        position: {
          x: getCenteredX(i, collections.length, 220, 50, containerWidth) + 50,
          y: COLLECTION_LAYER_Y,
        }, // example position, adjust as needed
        type: "category", // define this node type in your React Flow setup
        data: {
          label: collection.label,
          color,
          opacity,
          boxType: "collection",
        },
        style: {
          backgroundColor: color,
          color: textColor,
          opacity,
          padding: 10,
          borderRadius: 10,
          minWidth: 160,
          textAlign: "center",
          fontWeight: "medium",
        },
      });
    });

    // SYSTEM nodes (middle)
    filteredSystems.forEach((sys, i) => {
      const matchedCats = sys.categories
        .map((catId) => categories.find((c) => c.id === catId))
        .filter(Boolean);

      // const primaryColor = matchedCats[0]?.color || "#f5f5f5";
      // const textColor = getContrastingTextColor(primaryColor);

      const prioritizedCat = selectedCategoryId
        ? matchedCats.find((cat) => cat?.id === selectedCategoryId) ??
          matchedCats[0]
        : matchedCats[0];

      const primaryColor = prioritizedCat?.color || "#f5f5f5";
      const textColor = getContrastingTextColor(primaryColor);

      let border = usecase
        ? `7px dashed ${
            usecase === "effectiveness"
              ? `${effectivenessToHex(sys?.effectiveness)}`
              : usecase === "type"
              ? `${typeToHex(sys?.type)}`
              : ""
          }`
        : "";

      if (usecase)
        console.log({
          border,
          usecase,
          effectColor: effectivenessToHex(sys?.effectiveness),
          effect: sys?.effectiveness,
        });

      nodes.push({
        id: sys.id,
        position: {
          x:
            getCenteredX(i, filteredSystems.length, 200, 50, containerWidth) +
            50,
          y: SYSTEM_LAYER_Y,
        },
        data: {
          label: sys.label,
          // label: (
          // 	<div>
          // 		<div className="font-bold">{sys.label}</div>
          // 		<div className="flex flex-wrap justify-center gap-1 mt-1">
          // 			{matchedCats.map(cat => (
          // 				<span
          // 					key={cat?.id}
          // 					className={`text-xs px-2 py-0.5 rounded-full ${
          // 						cat?.color
          // 					} ${getContrastingTextColor(cat?.color)}`}>
          // 					{cat?.name}
          // 				</span>
          // 			))}
          // 		</div>
          // 	</div>
          // ),
          color: primaryColor,
          opacity:
            selectedCategoryId && !sys.categories.includes(selectedCategoryId)
              ? 0.2
              : 1,
          onInfoClick: () => {
            onClick && onClick(sys?.id);
          },
          border,
        },
        type: "category",
        style: {
          background: primaryColor,
          color: textColor,
          fontWeight: "bold",
          padding: 10,
          borderRadius: 10,
          minWidth: 160,
          textAlign: "center",
          cursor: "pointer",
          opacity: activeSystemId && activeSystemId !== sys.id ? 0.2 : 1,
        },
      });
    });

    // KPI nodes (top)
    visibleKPIs.forEach((kpi, i) => {
      // const catSystem = systems.find(s => s?.id === kpi.systemIds?.[0]);
      // const categoryMatch = categories.find(c =>
      // 	catSystem?.categories?.includes(c?.id)
      // );
      let catColor: any = null;

      if (kpi.systemIds?.length > 0) {
        let primarySystem = systems.find((s) => s.id === kpi.systemIds[0]);

        if (selectedCategoryId) {
          // If a category is selected, prioritize it
          catColor = categories.find((c) => c.id === selectedCategoryId);
        } else if (activeSystemId) {
          // If a system is selected, prioritize its first category
          const activeSystem = systems.find((s) => s.id === activeSystemId);
          if (activeSystem?.categories?.length > 0) {
            catColor = categories.find(
              (c) => c.id === activeSystem.categories[0]
            );
          }
        } else if (primarySystem) {
          // Fallback to default primary system category
          catColor = categories.find((c) =>
            primarySystem.categories.includes(c.id)
          );
        }
      }

      const color = catColor?.color || "#f5f5f5";
      const textColor = getContrastingTextColor(color);

      // Determine if this KPI is "connected"
      let isFaded = false;

      if (selectedCategoryId) {
        const connectedSystems = systems.filter((s) =>
          s.categories.includes(selectedCategoryId)
        );
        const connectedSystemIds = connectedSystems.map((s) => s.id);
        isFaded = !kpi.systemIds.some((id) => connectedSystemIds.includes(id));
      } else if (activeSystemId) {
        isFaded = !kpi.systemIds.includes(activeSystemId);
      }

      nodes.push({
        id: kpi?.id,
        position: {
          x: getCenteredX(i, visibleKPIs.length, 250, 50, containerWidth) + 50,
          y: KPI_LAYER_Y,
        },
        type: "category",
        data: {
          label: kpi?.label,
          color,
          opacity: isFaded ? 0.2 : 1,
        },
        style: {
          color: textColor,
          background: color,
          padding: 10,
          borderRadius: 10,
          minWidth: 160,
          textAlign: "center",
          fontWeight: "medium",
          opacity: isFaded ? 0.2 : 1,
        },
      });
    });
    return nodes;
  }, [
    collections,
    systems,
    categories,
    kpis,
    selectedCategoryId,
    activeSystemId,
    usecase,
  ]);

  // EDGES
  const edges: Edge[] = useMemo(() => {
    const edges: Edge[] = [];
    const edgeSet = new Set<string>();
    filteredSystems.forEach((sys) => {
      sys.categories.forEach((catId) => {
        const isFaded =
          (selectedCategoryId && catId !== selectedCategoryId) ||
          (activeSystemId && sys.id !== activeSystemId);

        let catColor = "#ccc";

        if (selectedCategoryId && sys.categories.includes(selectedCategoryId)) {
          catColor =
            categories.find((c) => c.id === selectedCategoryId)?.color ||
            "#ccc";
        } else {
          catColor =
            categories.find((c) => sys.categories.includes(c.id))?.color ||
            "#ccc";
        }

        const strokeColor = tailwindToHex(catColor);
        const edgeId = `edge-cat-${catId}->${sys.id}`;
        if (!edgeSet.has(edgeId)) {
          edgeSet.add(edgeId);
          edges.push({
            id: edgeId,
            source: `cat-${catId}`,
            target: sys.id,
            animated: false,
            type: "smoothstep",
            style: {
              strokeWidth: 3,
              stroke: strokeColor,
              opacity: isFaded ? 0.2 : 1,
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: strokeColor,
            },
          });
        }
      });
    });

    visibleKPIs.forEach((kpi) => {
      kpi.systemIds.forEach((sysId) => {
        if (filteredSystems) {
          // const catSystem = systems.find(c => c?.id === kpi?.systemIds?.[0]);
          // const catColor = categories.find(c =>
          // 	catSystem?.categories?.includes(c.id)
          // )?.color;

          const system = systems.find((s) => s.id === sysId);
          if (!system) return;

          let catColor = "#ccc";

          if (
            selectedCategoryId &&
            system.categories.includes(selectedCategoryId)
          ) {
            // If a category is selected and the system belongs to it
            catColor =
              categories.find((c) => c.id === selectedCategoryId)?.color ||
              "#ccc";
          } else if (!selectedCategoryId && activeSystemId === system.id) {
            // If a system is selected and no category is selected, use its first category
            const primaryCatId = system.categories?.[0];
            catColor =
              categories.find((c) => c.id === primaryCatId)?.color || "#ccc";
          } else {
            // Fallback to any matching category
            catColor =
              categories.find((c) => system.categories.includes(c.id))?.color ||
              "#ccc";
          }

          const strokeColor = tailwindToHex(catColor);

          const isFaded =
            (selectedCategoryId &&
              !system.categories.includes(selectedCategoryId)) ||
            (activeSystemId && sysId !== activeSystemId);
          const sourceNode = nodes.find((n) => n.id === sysId);
          const kpiNode = nodes.find((n) => n.id === kpi.id);

          if (sourceNode && kpiNode) {
            const edgeId = `edge-sys-${sysId}->kpi-${kpi.id}`;
            if (!edgeSet.has(edgeId)) {
              edgeSet.add(edgeId);
              edges.push({
                id: edgeId,
                source: sysId,
                target: kpi?.id,
                animated: false,
                type: "smoothstep",
                style: {
                  strokeWidth: 3,
                  stroke: strokeColor,
                  opacity: isFaded ? 0.2 : 1,
                },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: strokeColor,
                },
              });
            }
          }
        }
      });
    });

    collections.forEach((collection) => {
      // Edges from categories to collection
      collection.categories.forEach((catId) => {
        const edgeId = `cat-${catId}-to-collection-${collection.id}`;

        // Fade edge when category selected and this edge not connected
        let opacity = 1;
        if (selectedCategoryId) {
          opacity = catId === selectedCategoryId ? 1 : 0.2;
        }
        const isFaded = selectedCategoryId && catId !== selectedCategoryId;

        let catColor = "#ccc";

        if (
          selectedCategoryId &&
          collection.categories.includes(selectedCategoryId)
        ) {
          catColor =
            categories.find((c) => c.id === selectedCategoryId)?.color ||
            "#ccc";
        } else {
          catColor =
            categories.find((c) => collection.categories.includes(c.id))
              ?.color || "#ccc";
        }

        const strokeColor = tailwindToHex(catColor);

        // System selection does NOT fade collection edges
        if (!edgeSet.has(edgeId)) {
          edgeSet.add(edgeId);
          edges.push({
            id: edgeId,
            source: `cat-${catId}`,
            target: collection.id,
            animated: false,
            type: "smoothstep",
            style: {
              stroke: strokeColor,
              strokeWidth: 2,
              opacity,
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: strokeColor,
            },
            // arrowHeadType: "arrowclosed",
          });
        }
      });

      // Edges from collection to systems
      collection.systemIds.forEach((sysId) => {
        const edgeId = `collection-${collection.id}-to-system-${sysId}`;

        // Fade edge based on category or system selection
        let opacity = 1;

        if (selectedCategoryId) {
          // Fade if collection or system NOT connected to selectedCategoryId
          const system = systems.find((s) => s.id === sysId);
          const systemHasCategory =
            system?.categories.includes(selectedCategoryId);
          const collectionHasCategory =
            collection.categories.includes(selectedCategoryId);
          opacity = systemHasCategory && collectionHasCategory ? 1 : 0.2;
        } else if (activeSystemId) {
          // Fade if edge not connected to active system
          opacity = sysId === activeSystemId ? 1 : 0.2;
        }

        // Use collection color for stroke or fallback
        const color = collection.color || "#ccc";
        if (!edgeSet.has(edgeId)) {
          edgeSet.add(edgeId);
          edges.push({
            id: edgeId,
            source: collection.id,
            target: sysId,
            animated: false,
            type: "smoothstep",
            style: {
              stroke: color,
              strokeWidth: 2,
              opacity,
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color,
            },
            // arrowHeadType: "arrowclosed",
          });
        }
      });
    });
    return edges;
  }, [
    collections,
    systems,
    categories,
    kpis,
    selectedCategoryId,
    activeSystemId,
    usecase,
  ]);

  // const [nodesState, setNodesState, onNodesChange] = useNodesState(nodes);
  // const [edgesState, setEdgesState, onEdgesChange] = useEdgesState(edges);

  // // Optionally sync states when nodes or edges update
  // useEffect(() => {
  // 	setNodesState(nodes);
  // 	setEdgesState(edges);
  // }, [nodes, edges, usecase]);

  // Handle node clicks
  const handleNodeClick = (_: any, node: Node) => {
    const isCategoryNode = node?.id?.startsWith("cat-");
    const catId = isCategoryNode ? node?.id?.replace("cat-", "") : null;

    if (isCategoryNode) {
      setSelectedCategoryId((prev) => (prev === catId ? null : catId));
      setActiveSystemId(null);
    } else if (systems.find((s) => s?.id === node?.id)) {
      setActiveSystemId((prev) => (prev === node?.id ? null : node?.id));
      setSelectedCategoryId(null);
    }
  };

  if (!nodes.length || !edges.length) return <div>Loading...</div>;

  return (
    <div className="w-full h-[650px] border rounded" ref={containerRef}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        // onNodesChange={onNodesChange}
        // onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={{
          type: "smoothstep", // or "straight" "step"
          animated: false,
        }}
        fitView
        zoomOnScroll={false} // disables scroll zoom
      >
        <Background />
        <Controls />
      </ReactFlow>
    </div>
  );
};

export default FlowCanvas;
